import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

// In-app FAQ content. Single source of truth — the route components and the
// safety screen both pull from here. Keep the tone direct, imperative, and
// vague on internal tech (no "AI" / "model" / vendor names in answers).

export interface FaqQuestion {
  q: string;
  a: ReactNode;
}

export interface FaqSection {
  id: string;
  title: string;
  intro?: ReactNode;
  questions: FaqQuestion[];
}

export const FAQ_SECTIONS: FaqSection[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    questions: [
      {
        q: 'What is Menu to Menu?',
        a: <p>A travel tool that turns a photo of a foreign-language menu into something you can read, build an order from, and show your waiter in their language. Point. Tap. Eat.</p>,
      },
      {
        q: 'Who is it for?',
        a: <p>Travelers eating in restaurants where the menu is in a language you don't read. Especially useful with dietary restrictions or allergies.</p>,
      },
      {
        q: 'Do I need an account?',
        a: <p>No. Open the site and start using it. Sign in only to back up your saved menus and orders across devices.</p>,
      },
      {
        q: 'Is it free?',
        a: <p>Yes. No paywall, no ads, no scan limit today.</p>,
      },
      {
        q: 'What devices does it work on?',
        a: <p>Any modern phone with a camera and a browser. Optimized for mobile.</p>,
      },
      {
        q: 'Is there an app to download?',
        a: <p>Install Menu to Menu straight from your browser — see the <em>Installing and offline use</em> section below.</p>,
      },
    ],
  },
  {
    id: 'scanning',
    title: 'Scanning a menu',
    questions: [
      {
        q: 'How do I scan a menu?',
        a: <p>Tap "Take a photo of the menu" on the home screen. Frame the menu and capture. Add more pages if needed. Tap "Translate menu" when done.</p>,
      },
      {
        q: 'Tips for a good scan',
        a: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Fill the frame</strong> — get close enough that the menu fills the photo.</li>
            <li><strong>Keep it flat</strong> — press a folded menu open against the table.</li>
            <li><strong>Avoid glare</strong> — tilt the phone to kill reflections from glossy or laminated menus.</li>
            <li><strong>One page at a time</strong> — split a multi-page menu into separate captures.</li>
            <li><strong>Steady the phone</strong> — blur is the single biggest cause of bad reads.</li>
          </ul>
        ),
      },
      {
        q: 'How long does a scan take?',
        a: <p>Usually 10 to 40 seconds for a printed menu. Longer for very dense or multi-page menus. The app shows progress while it works.</p>,
      },
      {
        q: 'Can I upload a photo I took earlier?',
        a: <p>Yes — tap "Upload from photos" on the home screen and pick from your library.</p>,
      },
      {
        q: 'Multi-page menus?',
        a: <p>Capture each page as a separate photo, then tap "Translate menu" once all pages are added.</p>,
      },
      {
        q: 'Does it work on handwritten menus?',
        a: <p>Sometimes. Accuracy depends on legibility. Dishes the reader is unsure about are flagged with ⚠.</p>,
      },
      {
        q: 'Menus behind glass or under plastic?',
        a: <p>Glare is the enemy. Tilt the phone or move your body to kill the reflection before capturing.</p>,
      },
      {
        q: 'Hand-painted wall menus, chalkboards?',
        a: <p>Often, yes. Distance, lighting, and font matter. If the read is unreliable, photograph any printed menu nearby instead.</p>,
      },
      {
        q: 'Why did the app say "Doesn\'t look like a menu"?',
        a: <p>The image either isn't a menu, isn't readable, or doesn't have items the reader recognized. Reframe and try again, or upload a clearer photo.</p>,
      },
      {
        q: 'My photo is huge — will that be a problem?',
        a: <p>No. The app compresses photos automatically before sending.</p>,
      },
    ],
  },
  {
    id: 'reading',
    title: 'Reading the translated menu',
    questions: [
      {
        q: 'What do the ⚠ marks next to a dish mean?',
        a: <p>The reader was less confident about that dish. Tap the dish to see the original-language text alongside the translation.</p>,
      },
      {
        q: 'Why does the app say "Translation quality is low on this menu"?',
        a: <p>A large share of dishes came back uncertain. Often a sharper, flatter, better-lit retake fixes it.</p>,
      },
      {
        q: 'What about dietary or allergen badges?',
        a: <p>When the reader is confident, dishes get tagged with allergens (nuts, dairy, shellfish, etc.) and dietary categories. <strong>Missing badges don't mean a dish is safe.</strong> They only mean the reader didn't have enough information to flag it.</p>,
      },
      {
        q: 'Can I see the original language?',
        a: <p>Toggle the language switch at the top of the menu screen, or tap any dish to see both side-by-side.</p>,
      },
      {
        q: 'How are prices read?',
        a: <p>From the menu image. If a price is missing or unreadable, the dish shows "Ask" instead.</p>,
      },
      {
        q: 'What if the currency looks wrong?',
        a: <p>The currency is detected from symbols on the menu. Treat the printed prices on the actual menu as the source of truth — that's what you'll be billed.</p>,
      },
      {
        q: 'Why are some dishes hidden from view?',
        a: <p>Your allergen filter (set during onboarding or in Settings) hides dishes flagged as containing ingredients you've blocked. Tap "Show anyway" to see filtered dishes.</p>,
      },
      {
        q: 'Some dish names look strange in the translation',
        a: <p>Regional dishes, slang, and menu jargon don't always translate cleanly. The original-text view is the fallback — show the original name to the waiter to clarify.</p>,
      },
    ],
  },
  {
    id: 'safety',
    title: 'Allergies, dietary needs, and safety',
    intro: (
      <p style={{ fontWeight: 600, color: 'var(--color-danger)' }}>
        This is the most important section. Read it carefully — or jump to the focused{' '}
        <Link to="/safety" style={{ color: 'var(--color-danger)', textDecoration: 'underline' }}>
          Safety guide
        </Link>.
      </p>
    ),
    questions: [
      {
        q: 'Can I trust this app for severe or life-threatening allergies?',
        a: (
          <>
            <p><strong>No translation app is a substitute for a direct conversation with the kitchen.</strong> Use Menu to Menu as one layer of communication — pair it with verbal confirmation, written notes, and your own vigilance.</p>
            <p>If a reaction would land you in the hospital, do all of:</p>
            <ol style={{ paddingLeft: 18 }}>
              <li>Tag dishes as <strong>Strict / medical</strong>, not Preference.</li>
              <li>Show the source-language phrase to the waiter AND ask them to read it back.</li>
              <li>Have the waiter walk it to the kitchen and return with confirmation.</li>
              <li>Carry a translated allergy card or doctor's note in the local language as backup.</li>
              <li>Carry your medication (epinephrine, antihistamines, etc.) regardless.</li>
            </ol>
            <p>The app reduces communication friction. It does not reduce medical risk on its own.</p>
          </>
        ),
      },
      {
        q: 'What\'s the difference between "Preference" and "Strict / medical"?',
        a: (
          <>
            <ul style={{ paddingLeft: 18 }}>
              <li><strong>Preference</strong> is a polite question — <em>"Is this dish vegan?"</em> Yellow chip. The waiter may answer casually, even guess.</li>
              <li><strong>Strict / medical</strong> prefixes your tag with allergy framing — <em>"I have a severe nut allergy. Please confirm with the kitchen this dish has no nuts, no peanut oil, and is not cooked alongside nut dishes."</em> Red card. Designed to escalate to the kitchen.</li>
            </ul>
            <p><strong>Always pick Strict / medical for actual allergies.</strong> Preference is for taste choices, not safety.</p>
          </>
        ),
      },
      {
        q: 'What if my source language isn\'t fully supported for dietary phrases?',
        a: (
          <>
            <p>Some safety-critical phrases are still being reviewed by native speakers. When a phrase isn't yet finalized in the menu's source language, the app shows the <strong>English version with a clear warning</strong>: <em>"Shown in English — [language] translation coming soon."</em></p>
            <p>In that case:</p>
            <ul style={{ paddingLeft: 18 }}>
              <li>The waiter may not be able to read the English text.</li>
              <li><strong>Pair the English phrase with the red allergy banner</strong>, which IS in the source language.</li>
              <li>Point at the dish on your screen.</li>
              <li>Ask the waiter to find a colleague who reads English.</li>
              <li><strong>If you are not confident the message is getting through, do not order the dish.</strong></li>
            </ul>
            <p>This visible warning is intentional — better to see the gap and take extra care than have the chip silently disappear.</p>
          </>
        ),
      },
      {
        q: 'Which languages have native dietary phrasebooks today?',
        a: (
          <>
            <p><strong>Full native-language coverage:</strong> English, Spanish, French, Italian, German, Portuguese, Japanese, Chinese (Simplified and Traditional), Korean, Thai, Vietnamese.</p>
            <p><strong>English fallback today, native rolling in:</strong> Russian, Polish, Turkish, Greek, Arabic, Hebrew, Persian, Hindi, Dutch, Indonesian, Czech, Ukrainian, Malay, Filipino, Swedish, Danish, Norwegian, Finnish.</p>
            <p>The greeting, thank-you, and red ALLERGY banner are translated in many more languages. The medical phrases need human review before they're considered safe to ship.</p>
          </>
        ),
      },
      {
        q: 'What about cross-contamination?',
        a: (
          <>
            <p>Cross-contamination is one of the most under-recognized risks abroad. Shared oil, pans, cutting boards, and prep surfaces all carry trace amounts.</p>
            <p>The app has a dedicated <strong>"No cross-contamination"</strong> strict tag. Use it for celiac disease, severe nut allergies, severe shellfish allergies, severe dairy allergies. The kitchen card explicitly asks for separate prep surfaces and clean cookware.</p>
          </>
        ),
      },
      {
        q: 'What about hidden ingredients?',
        a: (
          <>
            <p>Lard in beans. Fish sauce in stir-fries. Stock cubes in vegetable soup. Whey powder in bread. Gelatin in desserts. Anchovies in Caesar dressing. Common across cuisines, almost never listed on the menu.</p>
            <p>The app surfaces dedicated tags for the most common cases:</p>
            <ul style={{ paddingLeft: 18 }}>
              <li><strong>No pork lard</strong> — Mexican beans, refried dishes, Latin American stews.</li>
              <li><strong>No fish sauce</strong> — Southeast Asian stir-fries, curries, soups.</li>
              <li><strong>No alcohol</strong> — sauces, marinades, slow-cooked dishes.</li>
            </ul>
            <p>If your concern is one of these, <strong>always use the strict tag</strong>, even on a dish that doesn't seem to involve the ingredient.</p>
          </>
        ),
      },
      {
        q: 'The app didn\'t flag a dish — does that mean it\'s safe for me?',
        a: (
          <>
            <p><strong>No. The app does not mark dishes "safe."</strong> It hides dishes that contain ingredients you've flagged when it's confident they're present. Absence of a flag is not a guarantee of absence.</p>
            <p>Allergens may be present but unlabeled, or unknown to the menu reader. Treat unflagged dishes the same way you'd treat any unfamiliar dish abroad — verify before eating.</p>
          </>
        ),
      },
      {
        q: 'What does the red allergy banner actually do?',
        a: <p>When you have hard-filter allergens set, the hand-off screen shows a bold red banner at the top, labeled "ALLERGY" in the source language, listing your allergens. It's the first thing the waiter sees. Pair it with per-dish dietary chips for full coverage.</p>,
      },
      {
        q: 'Can I add my own custom note to a dish?',
        a: <p>Yes — every dish in your cart has a "Note for kitchen" field. For safety-critical custom notes, type them in the source language using a separate translator and review carefully. The pre-written dietary phrases in the app are designed to be safer than ad-hoc translation.</p>,
      },
      {
        q: 'What stops me from ordering a dish that conflicts with my strict tag?',
        a: <p>If you tag a dish with a strict tag and the dish is known to contain that allergen, the app blocks the add and asks you to choose a different dish, drop the tag, or override. Override <strong>only</strong> when the kitchen has confirmed the dish is safe — never on assumption.</p>,
      },
      {
        q: 'My waiter doesn\'t read the source language either. What now?',
        a: (
          <>
            <p>It happens — the waiter may speak neither your language nor the menu's. In that case:</p>
            <ul style={{ paddingLeft: 18 }}>
              <li>Find a colleague or manager who reads the menu's source language.</li>
              <li>Ask them to translate your card to the chef.</li>
              <li>If no one in the restaurant can read your safety phrase, <strong>leave</strong>.</li>
            </ul>
            <p>A restaurant where no one can confirm what's in their food is not safe for a strict allergy.</p>
          </>
        ),
      },
      {
        q: 'Can I use this app for a child\'s allergy?',
        a: <p>Treat it as you would any allergy — a tool, not a substitute for direct kitchen confirmation. The medical-flavored phrasing works equally for adults and children.</p>,
      },
      {
        q: 'Can I set multiple dietary needs at once?',
        a: <p>Yes — multiple allergens hard-filtered (hide all dishes with these) plus per-dish dietary tags (this dish must be vegan; this dish must be gluten-free). The hand-off screen surfaces both.</p>,
      },
    ],
  },
  {
    id: 'handoff',
    title: 'The hand-off screen — showing the waiter',
    questions: [
      {
        q: 'What does the waiter see when I show them my phone?',
        a: (
          <>
            <p>A clean white screen, in their language, with:</p>
            <ol style={{ paddingLeft: 18 }}>
              <li>A polite greeting in the source language.</li>
              <li>A red ALLERGY banner if you've set hard-filter allergens.</li>
              <li>An italic disclaimer asking the waiter to confirm details with the kitchen.</li>
              <li>Your selected dishes with quantity, custom notes, and dietary chips.</li>
              <li>The total.</li>
              <li>A thank-you in the source language.</li>
            </ol>
          </>
        ),
      },
      {
        q: 'Why is the screen so big and bright?',
        a: <p>The screen wakes to maximum brightness so the waiter can read it at arm's length even in dim light. It also stays awake while open.</p>,
      },
      {
        q: 'What if the waiter just nods without actually reading it?',
        a: <p>Common in busy restaurants. For a strict dietary tag, ask the waiter to read the red card aloud or back to you. If they can't or won't, ask them to walk it to the kitchen. If neither happens, <strong>don't order the dish</strong> until someone confirms it.</p>,
      },
      {
        q: 'Can I send the order to the waiter electronically?',
        a: <p>No — the app is a hand-off, not a kitchen ticket. Show the screen; the waiter writes it down or repeats it back. Keeps you in control and works in any restaurant in the world.</p>,
      },
      {
        q: 'Does the waiter need to install the app?',
        a: <p>No. The waiter just reads your screen.</p>,
      },
      {
        q: 'Can the waiter scroll through other parts of the app?',
        a: <p>The order screen is a focused view — it scrolls within itself if the order is long, but doesn't navigate elsewhere.</p>,
      },
      {
        q: 'Can I undo "Order placed"?',
        a: <p>Yes — a brief undo prompt appears right after you tap "Order placed." After that, the order is in your saved history (still removable from there).</p>,
      },
      {
        q: 'Can I re-show a past order to a waiter?',
        a: <p>Yes — open it from Saved → Orders. Useful if a kitchen question comes up later in the meal.</p>,
      },
    ],
  },
  {
    id: 'saved',
    title: 'Saved menus and orders',
    questions: [
      {
        q: 'Where do my saved menus live?',
        a: <p>On your device by default. Sign in to back them up to your account and access them from another device.</p>,
      },
      {
        q: 'Why did a menu I scanned automatically show up in Saved?',
        a: <p>Menus auto-save when you scan one or place an order from one. Removes the friction of remembering to tap a Save button.</p>,
      },
      {
        q: 'Where do my orders live?',
        a: <p>On your device. Backed up to your account when signed in.</p>,
      },
      {
        q: 'Can I delete a saved menu or order?',
        a: <p>Saved menus and orders follow your scan/order history on the device. Sign out clears the device-side cart and orders. Granular delete is on the roadmap.</p>,
      },
      {
        q: 'Will my orders show across all my devices?',
        a: <p>Yes, once you sign in on each device. Orders saved while signed out get an "import these orders?" prompt the first time you sign in on a device.</p>,
      },
      {
        q: 'Can I share a menu I\'ve scanned with a friend?',
        a: <p>Today, no — sharing is a planned feature. For now, both of you can scan the menu independently.</p>,
      },
    ],
  },
  {
    id: 'account',
    title: 'Account, sign-in, and your data',
    questions: [
      {
        q: 'Why sign in?',
        a: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>Back up your saved menus and orders.</li>
            <li>Access them from any device.</li>
            <li>Get future features that need an account.</li>
          </ul>
        ),
      },
      {
        q: 'How does sign-in work?',
        a: <p>Enter your email. Get a 6-digit code. Type it into the app. Done. No password.</p>,
      },
      {
        q: 'How do I sign out?',
        a: <p>Settings → Account → Sign out.</p>,
      },
      {
        q: 'What does the app store about me?',
        a: <p>The minimum to give you the features you see — your reading-language preference, your dietary settings, your saved menus, your saved orders, and your sign-in email if you signed in.</p>,
      },
      {
        q: 'Are my menu photos kept?',
        a: <p>Photos taken to scan a menu are sent to be read into structured data. Don't include personal information in scans (a hand stuck in the photo, a credit card on the table, etc.).</p>,
      },
      {
        q: 'Will I get marketing emails?',
        a: <p>Sign-in codes only. The email field isn't used for outreach.</p>,
      },
    ],
  },
  {
    id: 'languages',
    title: 'Languages',
    questions: [
      {
        q: 'How many languages does the app support?',
        a: <p>Reading: 30+. Source (menu) language: 30+, auto-detected from the photo.</p>,
      },
      {
        q: 'Can I switch my reading language later?',
        a: <p>Yes — Settings → Reading language.</p>,
      },
      {
        q: 'Why are dietary phrases only finalized in some languages?',
        a: <p>Dietary safety phrases are hand-written per language and reviewed by native speakers before they're considered safe to ship. Until each language is reviewed, the app falls back to English with a visible warning.</p>,
      },
      {
        q: 'Will more languages get full dietary coverage?',
        a: <p>Yes — that's the active priority. Coverage expands as native-speaker reviewers complete each language.</p>,
      },
      {
        q: 'Does the app handle right-to-left languages (Arabic, Hebrew, Persian)?',
        a: <p>Yes — RTL text renders correctly in both the menu view and the hand-off screen.</p>,
      },
    ],
  },
  {
    id: 'pricing',
    title: 'Prices, currency, and the bill',
    questions: [
      {
        q: 'Does the price include tax?',
        a: <p>Prices are shown as they appear on the menu. Tax conventions vary — included in the EU, often added in the US. Confirm with the waiter or your receipt.</p>,
      },
      {
        q: 'Does the price include service or tip?',
        a: <p>Depends on the country and the restaurant. The total in the app is just the sum of the dish prices, not a final bill.</p>,
      },
      {
        q: 'What if my receipt doesn\'t match the app\'s total?',
        a: <p>Check for tax, service, cover charges, drinks, or items added at the table. The app's total is what you ordered through the hand-off screen — nothing more.</p>,
      },
      {
        q: 'What if a price on the menu changed?',
        a: <p>The app shows what was on the menu when you scanned it. The bill reflects the restaurant's current prices, not the cached ones.</p>,
      },
      {
        q: 'Can I split a bill?',
        a: <p>Not today.</p>,
      },
      {
        q: 'Does the app convert to my home currency?',
        a: <p>Not today. Prices are shown in the menu's local currency.</p>,
      },
    ],
  },
  {
    id: 'install',
    title: 'Installing and offline use',
    questions: [
      {
        q: 'Do I have to install the app?',
        a: <p>No — open menu2.menu in any browser. Installing is optional but recommended for travel.</p>,
      },
      {
        q: 'How do I install on iPhone?',
        a: (
          <ol style={{ paddingLeft: 18, margin: 0 }}>
            <li>Open menu2.menu in Safari (not Chrome or an in-app browser).</li>
            <li>Tap the Share button.</li>
            <li>Scroll and tap "Add to Home Screen."</li>
            <li>Tap Add.</li>
          </ol>
        ),
      },
      {
        q: 'How do I install on Android?',
        a: (
          <ol style={{ paddingLeft: 18, margin: 0 }}>
            <li>Open menu2.menu in Chrome.</li>
            <li>Tap the menu (three dots).</li>
            <li>Tap "Install app" or "Add to Home screen."</li>
          </ol>
        ),
      },
      {
        q: 'What does installing actually give me?',
        a: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>A home-screen icon — opens like a regular app.</li>
            <li>Faster cold start.</li>
            <li>Saved menus accessible offline.</li>
          </ul>
        ),
      },
      {
        q: 'Does the app work offline?',
        a: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Already-scanned menus</strong>: yes — view, browse, build orders without a connection.</li>
            <li><strong>Scanning a new menu</strong>: no — the menu image needs to be sent to the server.</li>
            <li><strong>Syncing orders to your account</strong>: no — needs a connection.</li>
            <li><strong>Showing the hand-off screen</strong>: yes, once the menu and order are built.</li>
          </ul>
        ),
      },
      {
        q: 'Does the app update itself?',
        a: <p>Yes — new versions roll out in the background. You'll see them the next time you open the app.</p>,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    questions: [
      {
        q: 'Camera won\'t open',
        a: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>Check your browser's camera permission for menu2.menu.</li>
            <li>On iPhone, the app must run in Safari (not in an in-app browser inside Instagram, Facebook, etc.).</li>
            <li>Try uploading a photo from your library instead.</li>
          </ul>
        ),
      },
      {
        q: 'Scan times out',
        a: <p>Slow connection or a very dense menu. Try again, or break a multi-page menu into smaller chunks.</p>,
      },
      {
        q: '"Doesn\'t look like a menu"',
        a: <p>Reframe so the menu fills the photo, kill any glare, and try again.</p>,
      },
      {
        q: '"Try again" with no clear reason',
        a: <p>Usually a network hiccup. Wait a moment and tap Retry.</p>,
      },
      {
        q: 'Saved menu disappeared',
        a: <p>If you scanned the menu while signed out and then cleared your browser data, it may have been removed. Sign in before scanning to back menus up to your account.</p>,
      },
      {
        q: 'App looks broken or styled strangely',
        a: <p>Force-refresh. The app may have updated mid-session.</p>,
      },
      {
        q: 'Cart wiped after navigating to another menu',
        a: <p>Today, your cart is bound to the menu you're actively viewing. Switching menus clears the cart. Improvements are on the roadmap.</p>,
      },
    ],
  },
];
